/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type Ref } from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { RovoLogo } from './components/RovoLogo';
import messages from './messages';
import type { AIButtonProps } from './types';

const atlaskitCssStyles = cssMap({
	pressableStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: '48px',
		width: '48px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.xxlarge'),
	},
	boxStyles: {
		width: '48px',
		height: '48px',
		flexShrink: 0,
		position: 'relative',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
		transition: 'transform 0.2s ease-in-out',
		willChange: 'transform',

		'&:hover': {
			transform: 'scale(1.125)',
			transition: 'transform 0.1s ease-in-out',
			cursor: 'pointer',
		},
		'&:active': {
			transform: 'scale(1.0625)',
		},
	},
});

export const AIButton = forwardRef<HTMLButtonElement, AIButtonProps>(
	({ onClick, isDisabled, onMouseEnter, onMouseLeave }, ref: Ref<HTMLButtonElement>) => {
		const { formatMessage } = useIntl();
		return (
			<div css={atlaskitCssStyles.boxStyles}>
				<Pressable
					testId="platform-ai-button"
					xcss={atlaskitCssStyles.pressableStyles}
					onClick={onClick}
					isDisabled={isDisabled}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onFocus={onMouseEnter}
					onBlur={onMouseLeave}
					ref={ref}
					interactionName="rovo-button-click"
				>
					<VisuallyHidden>{formatMessage(messages.rovoButton)}</VisuallyHidden>
					<RovoLogo />
				</Pressable>
			</div>
		);
	},
);
